import { animate, stagger } from "animejs";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { ActorDTO, Role, Runtime, TaskDTO } from "@roomate/core";
import { keys, useActors, useCreate, useCreateAgentImportPreview, useMe, useNodes } from "../api.ts";
import { onEscape } from "../lib/escape.ts";
import { defaultRuntime, SEAT_RUNTIMES } from "../lib/seats.ts";
import { useCurrentProject } from "../project.tsx";
import { fileToBase64, filesToZipBase64 } from "../agent-import.ts";
import { NewProjectDialog } from "../pages/Projects.tsx";
import { teamPeople } from "../roster.ts";
import { cx, ErrorNote } from "../ui.tsx";
import { NoWorkersBanner, useNoWorkerReason } from "./NoWorkersBanner.tsx";

/*
 * "Add new": one composer for everything a person starts. Each kind asks only what a human must say;
 * the rest is the agents' job (a bug becomes a spec with repro and acceptance criteria via spec-agent).
 */

type Kind = "idea" | "task" | "bug" | "agent" | "person" | "project";

/** Kinds only an admin may start. */
const ADMIN_KINDS: readonly Kind[] = ["agent", "person", "project"];

const KINDS: { kind: Kind; key: string; label: string; hint: string; glyph: ReactNode }[] = [
  { kind: "idea", key: "i", label: "Idea", hint: "One line. spec-agent writes the spec.", glyph: <Glyph d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /> },
  { kind: "task", key: "t", label: "Task", hint: "A title and what done means. Routed for you.", glyph: <Glyph d="M9 11l3 3 8-8M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /> },
  { kind: "bug", key: "b", label: "Bug", hint: "What broke. An agent writes the repro and the fix.", glyph: <Glyph d="M8 8V6a4 4 0 0 1 8 0v2M5 13H3m18 0h-2M6 19l-2 2m14-2 2 2M6 8h12v6a6 6 0 0 1-12 0V8zM12 20v-6" /> },
  { kind: "agent", key: "a", label: "Agent", hint: "A new teammate that connects through MCP, by default on someone's seat.", glyph: <Glyph d="M12 4v3M8 11h.01M16 11h.01M7 7h10a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-5a3 3 0 0 1 3-3zM9 15h6" /> },
  { kind: "person", key: "p", label: "Team member", hint: "Someone who approves, advises and steers.", glyph: <Glyph d="M16 19v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1M10 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 8v6M22 11h-6" /> },
  { kind: "project", key: "r", label: "Project", hint: "A new place to work: a fresh repo, a git URL or an existing path.", glyph: <Glyph d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" /> },
];

function Glyph({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

const typing = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
};

export function AddNew() {
  const me = useMe().data!;
  const [menu, setMenu] = useState(false);
  const [kind, setKind] = useState<Kind | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isAdmin = me.access === "admin";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "i") {
        e.preventDefault();
        setMenu(false);
        setKind("idea");
        return;
      }
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape") setMenu(false);
      if (!menu && !kind && e.key === "n") {
        e.preventDefault();
        setMenu(true);
      } else if (menu) {
        const k = KINDS.find((x) => x.key === e.key.toLowerCase());
        if (k && (isAdmin || !ADMIN_KINDS.includes(k.kind))) {
          e.preventDefault();
          setMenu(false);
          setKind(k.kind);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu, kind, isAdmin]);

  useEffect(() => {
    if (!menu || !menuRef.current) return;
    animate(menuRef.current, { opacity: [0, 1], translateY: [-6, 0], scale: [0.98, 1], duration: 260, ease: "outQuart" });
    animate(menuRef.current.querySelectorAll("li"), { opacity: [0, 1], translateX: [-4, 0], duration: 300, delay: stagger(30), ease: "outQuart" });
    const close = (e: MouseEvent) => !menuRef.current?.parentElement?.contains(e.target as Node) && setMenu(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  if (me.kind !== "human" || me.access === "viewer") return null;
  return (
    <div className="relative">
      <button
        onClick={() => setMenu((m) => !m)}
        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-ink px-3 py-1.5 text-[13px] text-paper transition-transform active:scale-[0.97]"
        aria-haspopup="menu"
        aria-expanded={menu}
        data-testid="add-new"
        title="Add new (N)"
      >
        <span className="text-[15px] leading-none">+</span> <span className="max-sm:sr-only">Add new</span>
      </button>
      {menu && (
        <div ref={menuRef} role="menu" className="glass absolute right-0 top-[calc(100%+8px)] z-50 w-[min(320px,calc(100vw-2rem))] rounded-[18px] !bg-card/95 p-2" style={{ transformOrigin: "top right" }}>
          <ul>
            {KINDS.filter((k) => isAdmin || !ADMIN_KINDS.includes(k.kind)).map((k) => (
              <li key={k.kind}>
                <button
                  role="menuitem"
                  onClick={() => (setMenu(false), setKind(k.kind))}
                  className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-ink/[0.05]"
                  data-testid={k.kind === "idea" ? "idea-button" : `add-${k.kind}`}
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-ink/[0.05] text-ink-2 group-hover:bg-ink group-hover:text-paper">{k.glyph}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-medium">{k.label}</span>
                    <span className="block truncate text-[12px] text-ink-3">{k.hint}</span>
                  </span>
                  <kbd className="os-kbd uppercase">{k.key}</kbd>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {kind && <Composer kind={kind} onClose={() => setKind(null)} />}
    </div>
  );
}

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) animate(ref.current, { opacity: [0, 1], translateY: [12, 0], scale: [0.98, 1], duration: 380, ease: "outExpo" });
    // Capture phase: the Sheet floats above any open Dialog, so one Escape closes only the Sheet.
    return onEscape(window, onClose, { capture: true });
  }, []);
  return (
    <div className="fixed inset-0 z-[60] grid place-items-start justify-items-center bg-ink/20 px-4 pt-[14vh] backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className="glass max-h-[calc(100dvh-14vh-1rem)] w-full max-w-[560px] overflow-y-auto rounded-[24px] !bg-card/95 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[18px] font-semibold tracking-[-0.02em]">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="text-ink-3 hover:text-ink">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const field = "w-full rounded-2xl border border-rule bg-paper/70 px-4 py-3 text-[15px] outline-none placeholder:text-ink-3 focus:border-ink-3 focus-visible:ring-2 focus-visible:ring-signal/50";

function Chips<T extends string>({ options, value, onChange, multi = false, label = (o) => o.replace(/[-_]/g, " ") }: { options: readonly T[]; value: T[]; onChange: (v: T[]) => void; multi?: boolean; label?: (o: T) => string }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(multi ? (on ? value.filter((x) => x !== o) : [...value, o]) : [o])}
            className={cx("rounded-full px-3 py-1 text-[13px] transition-colors", on ? "bg-ink text-paper" : "bg-ink/[0.05] text-ink-2 hover:bg-ink/[0.09]")}
          >
            {label(o)}
          </button>
        );
      })}
    </div>
  );
}

function Submit({ children, disabled, testId }: { children: ReactNode; disabled?: boolean; testId?: string }) {
  return (
    <button type="submit" disabled={disabled} className="rounded-full bg-ink px-5 py-2.5 text-[14px] text-paper transition-opacity disabled:opacity-40" data-testid={testId}>
      {children}
    </button>
  );
}

function Composer({ kind, onClose }: { kind: Kind; onClose: () => void }) {
  const { projectId } = useCurrentProject();
  if (kind === "idea" || kind === "bug") return <IdeaOrBug bug={kind === "bug"} projectId={projectId} onClose={onClose} />;
  if (kind === "task") return <NewTaskSheet projectId={projectId} onClose={onClose} />;
  if (kind === "project") return <NewProjectDialog onClose={onClose} />;
  return <NewActorSheet agent={kind === "agent"} onClose={onClose} />;
}

function IdeaOrBug({ bug, projectId, onClose }: { bug: boolean; projectId: string; onClose: () => void }) {
  const [text, setText] = useState("");
  const [expected, setExpected] = useState("");
  const [filed, setFiled] = useState<string | null>(null);
  // Live: the copy below says the work waits whenever the banner under it says no agent can work.
  const waiting = useNoWorkerReason() != null;
  const create = useCreate<{ specAgent: { name: string } | null }>("/ideas", [["intent"], ["project"], ["bridge"]]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const line = bug ? `Bug: ${text.trim()}${expected.trim() ? `. Expected: ${expected.trim()}` : ""}` : text.trim();
    create.mutate({ projectId, text: line }, { onSuccess: (r) => setFiled(r.specAgent ? `${r.specAgent.name} is on it.` : "Filed. Waiting for someone to write the spec.") });
  };
  return (
    <Sheet title={bug ? "Report a bug" : "What should we build?"} onClose={onClose}>
      {filed ? (
        <div className="space-y-4" data-testid="idea-filed">
          <p className="text-[15px]">
            {waiting
              ? bug
                ? "Saved. The reproduction, acceptance criteria and fix are waiting until an agent can work."
                : "Saved. The spec is waiting until an agent can work; then it will ask you only what it can't decide and send the spec for your approval."
              : `${filed} ${bug ? "It will write a reproduction, the acceptance criteria and the fix." : "It will ask you only what it can't decide, then send the spec for your approval."}`}
          </p>
          <NoWorkersBanner />
          <div className="flex gap-2">
            <Link to="/ideas" onClick={onClose} className="rounded-full bg-ink px-4 py-2 text-[13.5px] text-paper">
              Follow it on Ideas
            </Link>
            <button onClick={() => (setFiled(null), setText(""), setExpected(""))} className="rounded-full bg-ink/[0.05] px-4 py-2 text-[13.5px]">
              Another one
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <textarea
            autoFocus
            rows={2}
            className={field}
            placeholder={bug ? "What's broken? e.g. “The Office freezes when a run is stopped.”" : "One sentence is enough. e.g. “Let customers pay monthly.”"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && submit(e)}
            aria-label={bug ? "What's broken" : "Idea"}
            data-testid="idea-text"
          />
          {bug && <input className={field} placeholder="What should have happened? (optional)" value={expected} onChange={(e) => setExpected(e.target.value)} aria-label="Expected" />}
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12.5px] text-ink-3">{bug ? "Goes to spec-agent as a bug" : "Goes to spec-agent"} · ⌘↵</span>
            <Submit disabled={text.trim().length < 3 || !projectId || create.isPending} testId="idea-submit">
              {bug ? "Report" : "Send"}
            </Submit>
          </div>
          <ErrorNote error={create.error} />
        </form>
      )}
    </Sheet>
  );
}

function NewTaskSheet({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [done, setDone] = useState("");
  const create = useCreate<TaskDTO>("/tasks", [keys.project(projectId), ["bridge"]]);
  const navigate = useNavigate();
  const criteria = done.split("\n").map((l) => l.replace(/^[-*•]\s*/, "").trim()).filter(Boolean);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({ projectId, title: title.trim(), acceptanceCriteria: criteria, size: "s" }, { onSuccess: (t) => (onClose(), navigate(`/tasks/${t.id}`)) });
  };
  return (
    <Sheet title="New task" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <input autoFocus className={field} placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
        <textarea className={field} rows={4} placeholder={"Done when… (one per line)\nThe export button downloads an .xlsx\nTotals match the invoice list"} value={done} onChange={(e) => setDone(e.target.value)} aria-label="Done when" />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12.5px] text-ink-3">{criteria.length ? `${criteria.length} acceptance criteria · routed on create` : "Add at least one line so it can be routed"}</span>
          <Submit disabled={!title.trim() || create.isPending}>Create</Submit>
        </div>
        <ErrorNote error={create.error} />
      </form>
    </Sheet>
  );
}

const SKILLS = ["frontend", "backend", "e2e", "ci", "docs", "spec", "security", "design", "data"] as const;
const PEOPLE_ROLES: readonly Role[] = ["founder", "manager", "tech_lead", "pm", "developer", "qa", "ux"];
const FOCUS = ["security", "compliance", "gtm", "product", "data", "infra"] as const;
const ADAPTERS: readonly Runtime[] = SEAT_RUNTIMES;

function NewActorSheet({ agent, onClose }: { agent: boolean; onClose: () => void }) {
  const me = useMe().data!;
  const humans = teamPeople(useActors().data ?? []);
  const [name, setName] = useState("");
  const nodes = useNodes().data ?? [];
  // Default to the first ready runtime on the creator's node (claude-code if no node reported); a pick overrides it.
  const [pickedRuntime, setRuntime] = useState<Runtime[] | null>(null);
  const [ownerId, setOwnerId] = useState(me.id);
  const runtime: Runtime[] = pickedRuntime ?? [defaultRuntime(nodes, ownerId)];
  const [skills, setSkills] = useState<string[]>(agent ? ["frontend"] : []);
  const [role, setRole] = useState<Role[]>(["developer"]);
  const [focus, setFocus] = useState<string[]>([]);
  const [created, setCreated] = useState<ActorDTO | null>(null);
  const create = useCreate<ActorDTO>("/actors", [keys.actors]);
  const [agentTab, setAgentTab] = useState<"blank" | "import">("blank");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        kind: agent ? "agent" : "human",
        name: name.trim(),
        skills: skills.map((key) => ({ key, level: 4 })),
        ...(agent ? { runtime: runtime[0], ownerId, maxParallel: 100 } : { role: role[0], focus }),
      },
      { onSuccess: (a) => setCreated(a) },
    );
  };
  if (created)
    return (
      <Sheet title={`${created.name} is in the room`} onClose={onClose}>
        <p className="text-[15px]">{agent ? "Next: connect it from its card on Team, so a node can run it." : "Next: issue them a token from their card on Team."}</p>
        <div className="mt-4">
          <Link to="/team" onClick={onClose} className="rounded-full bg-ink px-4 py-2 text-[13.5px] text-paper">
            Open Team
          </Link>
        </div>
      </Sheet>
    );
  return (
    <Sheet title={agent ? "New agent" : "New team member"} onClose={onClose}>
      {agent && (
        <div className="mb-5 flex gap-1 rounded-xl bg-ink/[0.05] p-1" role="tablist" aria-label="Agent creation method">
          <button type="button" role="tab" aria-selected={agentTab === "blank"} onClick={() => setAgentTab("blank")} className={cx("flex-1 rounded-lg px-3 py-2 text-[13px]", agentTab === "blank" ? "bg-card font-medium shadow-sm" : "text-ink-2")} data-testid="agent-start-blank">Start blank</button>
          <button type="button" role="tab" aria-selected={agentTab === "import"} onClick={() => setAgentTab("import")} className={cx("flex-1 rounded-lg px-3 py-2 text-[13px]", agentTab === "import" ? "bg-card font-medium shadow-sm" : "text-ink-2")} data-testid="agent-import-tab">Import a Claude folder</button>
        </div>
      )}
      {agent && agentTab === "import" ? <AgentImportStart onClose={onClose} /> :
      <form onSubmit={submit} className="space-y-4">
        <input autoFocus className={field} placeholder={agent ? "Name, e.g. billing-agent" : "Name"} value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
        {agent ? (
          <>
            <div>
              <p className="mb-2 text-[12.5px] text-ink-2">Adapter</p>
              <Chips options={ADAPTERS} value={runtime} onChange={setRuntime} />
            </div>
            <div>
              <p className="mb-2 text-[12.5px] text-ink-2">Owner</p>
              <Chips options={humans.map((h) => h.id)} value={[ownerId]} onChange={(v) => setOwnerId(v[0]!)} label={(id) => humans.find((h) => h.id === id)?.name ?? id} />
              <p className="mt-1 text-[12px] text-ink-3">{humans.find((h) => h.id === ownerId)?.name} gets its questions; whose seat it runs on by default — change in Run config.</p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="mb-2 text-[12.5px] text-ink-2">Role</p>
              <Chips options={PEOPLE_ROLES} value={role} onChange={setRole} />
            </div>
            <div>
              <p className="mb-2 text-[12.5px] text-ink-2">Focus</p>
              <Chips options={FOCUS} value={focus as (typeof FOCUS)[number][]} onChange={setFocus} multi />
            </div>
          </>
        )}
        <div>
          <p className="mb-2 text-[12.5px] text-ink-2">Skills</p>
          <Chips options={SKILLS} value={skills as (typeof SKILLS)[number][]} onChange={setSkills} multi />
        </div>
        <div className="flex justify-end">
          <Submit disabled={!name.trim() || create.isPending}>Add</Submit>
        </div>
        <ErrorNote error={create.error} />
      </form>}
    </Sheet>
  );
}

export function AgentImportStart({ onClose, actorId }: { onClose: () => void; actorId?: string }) {
  const [sourceKind, setSourceKind] = useState<"upload" | "git">("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [url, setUrl] = useState("");
  const [ref, setRef] = useState("");
  const [subpath, setSubpath] = useState("");
  const [packing, setPacking] = useState(false);
  const preview = useCreateAgentImportPreview();
  const navigate = useNavigate();
  const pick = (list: FileList | null) => setFiles(list ? Array.from(list) : []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPacking(true);
    try {
      const source = sourceKind === "git"
        ? { kind: "git" as const, url: url.trim(), ...(ref.trim() ? { ref: ref.trim() } : {}), ...(subpath.trim() ? { subpath: subpath.trim() } : {}) }
        : { kind: "zip" as const, dataBase64: files.length === 1 && files[0]!.name.toLowerCase().endsWith(".zip") ? await fileToBase64(files[0]!) : await filesToZipBase64(files) };
      preview.mutate({ source, ...(actorId ? { actorId } : {}) }, { onSuccess: (result) => { onClose(); navigate(`/agents/import/${result.id}`); } });
    } finally {
      setPacking(false);
    }
  };
  const canSubmit = sourceKind === "git" ? /^https:\/\//.test(url.trim()) : files.length > 0;
  return (
    <form className="space-y-4" onSubmit={submit} data-testid="agent-import-start">
      <div className="flex gap-1">
        <button type="button" onClick={() => setSourceKind("upload")} className={cx("rounded-full px-3 py-1 text-[12.5px]", sourceKind === "upload" ? "bg-ink text-paper" : "bg-ink/[0.05]")}>ZIP or folder</button>
        <button type="button" onClick={() => setSourceKind("git")} className={cx("rounded-full px-3 py-1 text-[12.5px]", sourceKind === "git" ? "bg-ink text-paper" : "bg-ink/[0.05]")}>Git URL</button>
      </div>
      {sourceKind === "upload" ? (
        <div
          className="rounded-[18px] border border-dashed border-ink-3/60 bg-paper/50 p-6 text-center transition-colors hover:border-ink"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => { event.preventDefault(); pick(event.dataTransfer.files); }}
          data-testid="agent-import-dropzone"
        >
          <p className="text-[14px] font-medium">Drop a .zip or Claude folder</p>
          <p className="mt-1 text-[12px] text-ink-3">Folders are packaged in your browser before upload.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <label className="cursor-pointer rounded-full bg-ink px-4 py-2 text-[13px] text-paper">Choose .zip<input type="file" accept=".zip,application/zip" className="sr-only" onChange={(event) => pick(event.target.files)} data-testid="agent-import-zip" /></label>
            <label className="cursor-pointer rounded-full bg-ink/[0.06] px-4 py-2 text-[13px]">Choose folder<input type="file" multiple className="sr-only" onChange={(event) => pick(event.target.files)} {...({ webkitdirectory: "", directory: "" } as object)} data-testid="agent-import-folder" /></label>
          </div>
          {files.length > 0 && <p className="mt-3 break-all font-mono text-[11.5px] text-ink-2">{files.length === 1 ? files[0]!.name : `${files.length} files selected`}</p>}
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block text-[12.5px] text-ink-2">HTTPS git URL<input type="url" required className={cx(field, "mt-1.5 font-mono text-[13px]")} placeholder="https://github.com/org/agent.git" value={url} onChange={(event) => setUrl(event.target.value)} data-testid="agent-import-git-url" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[12.5px] text-ink-2">Ref · optional<input className={cx(field, "mt-1.5 font-mono text-[13px]")} placeholder="main or v1.2.0" value={ref} onChange={(event) => setRef(event.target.value)} /></label>
            <label className="text-[12.5px] text-ink-2">Subpath · optional<input className={cx(field, "mt-1.5 font-mono text-[13px]")} placeholder="packages/agent" value={subpath} onChange={(event) => setSubpath(event.target.value)} /></label>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12px] text-ink-3">You will review instructions, tools and every executable item before anything is applied.</p>
        <Submit disabled={!canSubmit || packing || preview.isPending} testId="agent-import-preview-submit">{packing ? "Packaging…" : preview.isPending ? "Inspecting…" : "Review import"}</Submit>
      </div>
      <ErrorNote error={preview.error} />
    </form>
  );
}
